import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { ChatPage } from './Chat';

interface ChatSessionInfo {
  id: string;
  title?: string;
  key: string;
  size: number;
  uploaded: string;
}

export function SessionsPage() {
  const [sessions, setSessions] = useState<ChatSessionInfo[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [enableMemory, setEnableMemory] = useState(localStorage.getItem('enableMemory') === 'true');
  const [hoverId, setHoverId] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem('enableMemory', enableMemory.toString());
  }, [enableMemory]);

  useEffect(() => {
    api.getSessions().then(data => {
      if (data && data.sessions) {
        setSessions(data.sessions);
      }
      setLoading(false);
    }).catch(e => {
      console.error('Failed to load sessions', e);
      setLoading(false);
    });
  }, []);

  const handleRename = async (e: React.MouseEvent, id: string, currentTitle: string) => {
    e.stopPropagation();
    const newTitle = window.prompt('Nhập tên mới cho đoạn chat:', currentTitle);
    if (newTitle && newTitle.trim() !== '' && newTitle !== currentTitle) {
      try {
        await api.renameSession(id, newTitle.trim());
        setSessions(prev => prev.map(s => s.id === id ? { ...s, title: newTitle.trim() } : s));
      } catch (err) {
        alert('Lỗi khi đổi tên: ' + err);
      }
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Bạn có chắc chắn muốn xóa đoạn chat này không? Hành động này không thể hoàn tác.')) {
      try {
        await api.deleteSession(id);
        setSessions(prev => prev.filter(s => s.id !== id));
        if (selectedId === id) setSelectedId(null);
      } catch (err) {
        alert('Lỗi khi xóa: ' + err);
      }
    }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
        <div>
          <h1 className="page-title">Lịch sử đoạn chat</h1>
          <p className="page-subtitle">Tất cả các phiên chat đã được lưu trên Cloudflare R2</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: 500 }}>Lưu Lịch sử: </span>
          <label className="switch">
            <input 
              type="checkbox" 
              checked={enableMemory}
              onChange={(e) => setEnableMemory(e.target.checked)}
            />
            <span className="slider"></span>
          </label>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {enableMemory ? 'ON (Đang lưu chat)' : 'OFF (Không lưu chat)'}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '16px', height: 'calc(100vh - 160px)' }}>
        {/* Left Sidebar: Treeview */}
        <div className="card" style={{ padding: '0', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)', fontWeight: 600 }}>
            <span style={{ marginRight: '8px' }}>📝</span>
            Danh sách Lịch sử
          </div>
          
          <div style={{ overflowY: 'auto', flex: 1, padding: '8px' }}>
            {loading ? (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
            ) : sessions.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                Chưa có lịch sử nào. Hãy bật ON Lịch sử và bắt đầu chat!
              </div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {sessions.map(session => (
                  <li 
                    key={session.id} 
                    onMouseEnter={() => setHoverId(session.id)}
                    onMouseLeave={() => setHoverId(null)}
                    style={{ position: 'relative' }}
                  >
                    <button
                      onClick={() => setSelectedId(session.id)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: '12px 14px',
                        background: selectedId === session.id ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                        border: 'none',
                        borderLeft: selectedId === session.id ? '4px solid var(--primary)' : '4px solid transparent',
                        borderRadius: '0 6px 6px 0',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        transition: 'all 0.2s',
                        color: selectedId === session.id ? 'var(--primary)' : 'var(--text-primary)',
                        boxShadow: selectedId === session.id ? 'inset 0 0 10px rgba(99,102,241,0.05)' : 'none'
                      }}
                    >
                      <div style={{ fontWeight: selectedId === session.id ? 700 : 500, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: hoverId === session.id ? '40px' : '0' }}>
                        {session.title || session.id}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {new Date(session.uploaded).toLocaleString()}
                        {' • '}
                        {(session.size / 1024).toFixed(1)} KB
                      </div>
                    </button>
                    {hoverId === session.id && (
                      <div style={{ position: 'absolute', top: '10px', right: '8px', display: 'flex', gap: '4px' }}>
                        <button 
                          onClick={(e) => handleRename(e, session.id, session.title || session.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', padding: '2px', opacity: 0.7 }}
                          title="Đổi tên"
                        >
                          ✏️
                        </button>
                        <button 
                          onClick={(e) => handleDelete(e, session.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', padding: '2px', opacity: 0.7 }}
                          title="Xóa"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right Pane: Chat Window */}
        <div className="card" style={{ padding: '16px', overflow: 'hidden', position: 'relative' }}>
          {selectedId ? (
            <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
              <ChatPage key={selectedId} loadSessionId={selectedId} hideHeader={true} />
            </div>
          ) : (
             <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
                <div style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-primary)' }}>Chọn một đoạn chat để xem lại</div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>Bạn có thể tiếp tục chat nối tiếp vào ngữ cảnh cũ</div>
             </div>
          )}
        </div>
      </div>
    </div>
  );
}
